import { makeAutoObservable } from 'mobx';

export type AudioRecorderState = {
  recording: boolean;
};

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;

  state: AudioRecorderState = {
    recording: false,
  };

  constructor() {
    makeAutoObservable(this);
  }

  static fileExtensionForMimeType(mimeType?: string) {
    const cleanMimeType = mimeType?.split(';')[0]?.toLowerCase();
    switch (cleanMimeType) {
      case 'audio/webm':
        return 'webm';
      case 'audio/ogg':
        return 'ogg';
      case 'audio/mp4':
        return 'mp4';
      case 'audio/mpeg':
        return 'mp3';
      case 'audio/wav':
      case 'audio/wave':
      case 'audio/x-wav':
        return 'wav';
      default:
        return 'webm';
    }
  }

  private getSupportedMimeType() {
    const supportedTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4',
    ];

    return supportedTypes.find(
      (mimeType) =>
        typeof MediaRecorder !== 'undefined' &&
        MediaRecorder.isTypeSupported(mimeType),
    );
  }

  async start() {
    if (this.state.recording) {
      throw new Error('Cannot start a recording while other in progress');
    }

    return new Promise<Blob>((resolve) => {
      (async () => {
        try {
          this.state.recording = true;

          const audioChunks: BlobPart[] = [];
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });

          const mimeType = this.getSupportedMimeType();
          this.mediaRecorder = new MediaRecorder(
            stream,
            mimeType ? { mimeType } : undefined,
          );

          this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
            audioChunks.push(event.data);
          };

          this.mediaRecorder.onstop = async () => {
            const audioBlob = new Blob(audioChunks, {
              type: this.mediaRecorder?.mimeType || mimeType || 'audio/webm',
            });
            stream.getTracks().forEach((track) => track.stop());
            resolve(audioBlob);
            this.state.recording = false;
          };

          this.mediaRecorder.start();
        } catch (e) {
          this.state.recording = false;
          throw e;
        }
      })();
    });
  }

  stop() {
    this.mediaRecorder?.stop();
  }
}
